<template>
  
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredDetails"
      :loading="isLoading"
      :no-data-text="noDataText"
      item-key="id"
      :sort-by="['buttonId']"
      class="elevation-2"
      >

      <template v-slot:top>
        <v-toolbar dense dark flat :color="appColor_Medium">
          
          <app-back-button v-bind:color="appColor_Dark" />

          <v-toolbar-title>Genie Configuration</v-toolbar-title>

          <v-spacer />
          
          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog
              v-model="editDialog"
              max-width="400px"
              :persistent="true"
              >
              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice" small dark :color="appColor_Dark" slot="activator" :disabled="disableAdd" class="mx-1 mr-0 elevation-0" v-on="on">New</v-btn>
                <v-btn v-else slot="activator" :color="appColor_Dark" small dark class="mx-1 mr-0 elevation-0" :disabled="disableAdd" v-on="on">Add Detail</v-btn>
              </template>
              <v-card>
                <v-toolbar dense dark flat :color="appColor_Medium">
                  <v-toolbar-title><v-icon>edit</v-icon> {{ formTitle }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="pa-0 pt-2 ma-0">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-select
                            v-model="editedDetail.buttonId"
                            item-value="id"
                            item-text="description"
                            :items="availableButtons"
                            label="Button"
                            v-validate="'required'"
                            :error-messages="errors.collect('button')"
                            data-vv-name="button"
                            ref="button"
                            :disabled="!isConfigurable(editedDetail) && editedDetail.id > 0"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" sm="12">
                          <v-select
                            v-model="editedDetail.buttonActionId"
                            item-value="id"
                            item-text="description"
                            :items="availableActions"
                            label="Action"
                            v-validate="'required'"
                            :error-messages="errors.collect('buttonAction')"
                            data-vv-name="buttonAction"
                            :disabled="!isConfigurable(editedDetail) && editedDetail.id > 0"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedDetail.description"
                            label="Description"
                            v-validate="'max:50'"
                            :error-messages="errors.collect('description')"
                            data-vv-name="description"
                            >
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>Save</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-1">

          <!-- Button Configuration -->
          <v-col cols="12" md="3">
            <v-select
              v-model="configId"
              item-value="id"
              item-text="name"
              :items="configs"
              label="Button Configuration"
              placeholder="Button Configuration"
              @change="loadAvailableArrays()"
              :disabled="isLoading"
            />
          </v-col>

          <v-col cols="12" md="9">
            <v-text-field
              v-model="selectedConfigDescription"
              readonly
              outlined
              :disabled="isLoading"
              dense
              style="font-size: 1em;"
              :class="!isSmallDevice ? 'ml-3 pt-2' : ''"
            />
          </v-col>
        </v-row>

      </template>

      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:item.action="{ item }">
        <span style="white-space: nowrap">
          <v-icon class="mr-3" @click="edit(item)">edit</v-icon>
          <template v-if="isConfigurable(item)">
          <v-icon @click="remove(item)">delete</v-icon>
          </template>
          <v-icon v-else color="grey lighten-2">delete</v-icon>
        </span>
      </template>
      
    </v-data-table>

  </v-container>
</template>

<script>
import appService from '@/app.service'
import { AppColor } from '@/config.js'
import AppBackButton from '@/components/App/AppBackButton'

export default {
  name: 'ConfigManager',
  components: {
    AppBackButton
  },
  props: {
    isAdminDashboard: {
      type: Boolean,
      default: false
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {

      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      editDialog: false,

      configId: null,
      configs: [],
      configDetails: [],
      buttons: [],
      availableButtons: [],
      buttonActions: [],
      availableActions: [],

      headers: [
        {
          text: 'Button',
          value: 'button',
          align: 'left',
          sortable: false,
          width: '20%',
          class: "pr-0"
        },
        {
          text: 'Action',
          value: 'buttonAction',
          align: 'left',
          sortable: false,
          width: '30%',
          class: "pr-0"
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          sortable: false,
          width: '40%',
          class: "pr-0"
        },
        {
          value: 'action',
          sortable: false,
          width: '10%'
        }
      ],
      editedIndex: -1,
      editedDetail: {
        id: -1,
        configId: null,
        description: null,
        buttonId: null,
        buttonActionId: null,
      },
      defaultDetail: {
        id: -1,
        description: null,
        configId: null,
        buttonId: null,
        buttonActionId: null,
      },
      dictionary: {
        custom: {
          description: {
            max: 'Description cannot exceed 50 characters'
          },
          button: {
            required: () => 'Button is required'
          },
          buttonAction: {
            required: () => 'Button Action is required'
          }
        }
      },
    }
  },
  computed: {
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.filteredDetails.length === 0
        ? 'No details found'
        : ''
    },
    formTitle () {
      return this.editedDetail.id < 0 ? 'New Detail' : 'Edit Detail'
    },
    filteredDetails () {
      const self = this
      return self.configDetails.filter(cd => {
        return cd.configId === self.configId
      })
    },
    buttonsMomentary () {
      return this.buttons.filter(b => b.isConfigurable)
    },
    selectedConfigDescription () {
      if (this.configs.length === 0) null
      const self = this
      const configs = self.configs.filter(c => c.id === self.configId)
      return configs.length > 0
        ? configs[0].description
        : null
    },
    disableAdd () {
      var disabled = this.buttons.length === 0 || this.buttonActions.length === 0
      if (!disabled)
        disabled = this.availableButtons.length === 0 || this.availableActions.length === 0

      return disabled || this.editedIndex >= 0
    }
  },
  methods: {
    loadData () {
      
      const self = this
      self.isLoading = true

      appService.getButtonConfigsForManager().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.configs = data.configs
          self.buttons = data.buttons
          self.buttonActions = data.buttonActions
          
          // map the details
          self.configDetails = data.configDetails.map(cd => { return {
            id: cd.id,
            configId: cd.configId,
            description: cd.description,
            buttonId: cd.buttonId,
            buttonActionId: cd.buttonActionId,
            button: self.getButton(cd.buttonId).description,
            buttonAction: self.getButtonAction(cd.buttonActionId).description
          }})

          // set default configId
          self.configId = self.configs.length > 0
            ? self.configs[0].id
            : null

          // arrays to help to determine if the Add button should be enabled/disabled
          self.loadAvailableArrays()
          self.isLoading = false
      }
      }).catch(e => {
        self.handleCriticalError(e)
      })
    },

    loadAvailableArrays () {
      this.loadAvailableButtons()
      this.loadAvailableActions()
    },

    loadAvailableButtons () {
      if (this.filteredDetails.length === 0) return

      const self = this
      const usedIds = self.filteredDetails
        .filter(cd => {
          const button = self.buttons.filter(b => b.id === cd.buttonId)[0]
          return button.isConfigurable
        })
        .map(cd => cd.buttonId)

      self.availableButtons = self.buttonsMomentary.filter(b => {
        return !usedIds.includes(b.id) ||
          (b.id === self.editedDetail.buttonId && self.editedDetail.id > 0)
      })
    },

    loadAvailableActions () {
      if (this.filteredDetails.length === 0) return

      const self = this
      const usedIds = self.filteredDetails
        .filter(cd => {
          const buttonAction = self.buttonActions.filter(b => b.id === cd.buttonActionId)[0]
          return !buttonAction.isSystem
        })
        .map(cd => cd.buttonActionId)

      self.availableActions = self.buttonActions.filter(ba => {
        return (!usedIds.includes(ba.id) && !ba.isSystem) ||
          (ba.id === self.editedDetail.buttonActionId && self.editedDetail.id > 0)
      })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.configDetails.findIndex(cd => cd.id === item.id)
      self.editedDetail = Object.assign({}, item)

      const button = self.getButton(item.buttonId)
      if (button.isConfigurable) {
        self.loadAvailableArrays()
      } else {
        self.availableButtons = self.buttons.filter(b => b.id === item.buttonId)
        self.availableActions = self.buttonActions.filter(b => b.id === item.buttonActionId)
      }

      self.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.configDetails.findIndex(cd => cd.id === item.id)
      if (index < 0) return

      const detail = self.configDetails[index]
      const button = detail.button
      const action = detail.buttonAction

      self.$confirm.show({
        title: 'Delete Detail?',
        text: `<b>${button} -> ${action}</b>`,
        onConfirm: () => {
          self.isDeleting = true
          appService.deleteButtonConfigDetail(item.id).then((data) => {
            self.isDeleting = false
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.configDetails.splice(index, 1)
              self.loadAvailableArrays()
            }
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const editedIndex = self.editedIndex
          self.isSaving = true

          if (editedIndex >= 0) {
            var editedDetail = self.configDetails[self.editedIndex]
            Object.assign(editedDetail, self.editedDetail)

            const configModel = self.getDetailModel(editedDetail)
            appService.updateButtonConfigDetail(editedDetail.id, configModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                self.configDetails[editedIndex].button = self.getButton(data.configDetail.buttonId).description
                self.configDetails[editedIndex].buttonAction = self.getButtonAction(data.configDetail.buttonActionId).description
                self.loadAvailableArrays()
              }
              self.isSaving = false
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            appService.addButtonConfigDetail(self.getDetailModel(self.editedDetail)).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedDetail.id = data.configDetail.id
                self.editedDetail.configId = self.configId
                self.editedDetail.button = self.getButton(data.configDetail.buttonId).description
                self.editedDetail.buttonAction = self.getButtonAction(data.configDetail.buttonActionId).description

                const index = self.configDetails
                  .findIndex(cd => {
                    const buttonId = self.editedDetail.buttonId > 0
                      ? self.editedDetail.buttonId - 1
                      : 0
                    return cd.buttonId === buttonId && cd.configId === self.configId
                  })

                self.configDetails.splice(index, 0, self.editedDetail)
                self.loadAvailableArrays()
              }
              self.isSaving = false
              self.closeDialog()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },
    
    getButton (buttonId) {
      const buttons = this.buttons.filter(b => b.id === buttonId)
      return buttons.length > 0
        ? buttons[0]
        : null
    },

    getButtonAction (actionId) {
      const actions = this.buttonActions.filter(ba => ba.id === actionId)
      return actions.length > 0
        ? actions[0]
        : null
    },

    getDetailModel (cd) {
      const description = (cd.description ?? '').trim()
      return {
        configId: this.configId,
        description: description.length > 0 ? description : null,
        buttonId: cd.buttonId,
        buttonActionId: cd.buttonActionId
      }
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedConfig()
        self.loadAvailableArrays()
      }, 300)
    },

    resetEditedConfig () {
      this.editedDetail = Object.assign({}, this.defaultDetail)
      this.editedIndex = -1
      this.$validator.reset()
    },

    isConfigurable (item) {
      if (item.id <= 0) return false
      if (this.buttons.length === 0) return false
      const button = this.buttons.filter(b => b.id === item.buttonId)[0]
      return button.isConfigurable
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
                      ? 'Validation Failed'
                      : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to button dropdown in the edit dialog
          self.$refs.button.focus()
        }, 300)
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>
